import { Suspense, useEffect, useTransition } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useHasAdminRole } from "services/auth/useHasAdminRole";
import { SqlRbacAction } from "services/rbac/action";
import { PrivilegeObjectType } from "services/rbac/types";
import { ServiceAccount } from "services/serviceAccounts/types";
import { useUsers } from "services/users/useUsers";

import { useCurrentAccount } from "components/Account/useCurrentAccount";
import { useAccessManager } from "components/App/accessManager";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import FormRow from "components/FormRow";
import formRowStyles from "components/FormRow/FormRow.module.scss";
import OutlinedSelect from "components/OutlinedSelect";
import Spinner from "components/Spinner";

import { AssignType, CREATE_NEW, Fields } from "./schema";

const UsersInput = (props: { form: UseFormReturn<Fields> }) => {
  const { form } = props;
  const { watch, control, setValue } = form;
  const [isPending, startTransition] = useTransition();
  const isOrganizationAdmin = useHasAdminRole();
  const { isAllowedTo } = useAccessManager();
  const { getAccount } = useCurrentAccount();
  const account = getAccount();

  const accountName = watch("accountName");
  const users = useUsers({ accountName });
  const userItems = users.filter(
    user =>
      (!user.serviceAccountName && !user.loginName) ||
      user.userName === form?.formState?.defaultValues?.assign?.[accountName] ||
      user.serviceAccountName === form?.formState?.defaultValues?.name
  );

  const assignedUser = users.find(
    user => user.serviceAccountName === form?.formState?.defaultValues?.name
  );

  const canCreateUser =
    isOrganizationAdmin ||
    isAllowedTo(
      PrivilegeObjectType.account,
      account.accountName,
      SqlRbacAction.USER_CREATE
    );

  useEffect(() => {
    if (assignedUser?.serviceAccountName) {
      const currentValue = watch("assign");
      setValue("assign", {
        ...currentValue,
        [accountName]: assignedUser?.userName,
      });
    }
  }, [
    accountName,
    assignedUser?.serviceAccountName,
    assignedUser?.userName,
    setValue,
    watch,
  ]);

  if (isPending) {
    return (
      <OutlinedSelect
        disabled
        prefix={<Spinner size={12} />}
      />
    );
  }

  return (
    <Controller
      control={control}
      name="assign"
      render={({ field: { onChange, value } }) => {
        const items = [
          ...userItems.map(user => {
            const { userName } = user;
            return (
              <ContextMenuItem
                value={userName}
                key={userName}
                testId={`user-${userName}`}
                checked={userName === value[accountName]}
                checkedIconPlaceholder={true}
                text={userName}
              />
            );
          }),
        ];

        if (canCreateUser) {
          items.unshift(
            <ContextMenuItem
              value={CREATE_NEW}
              key={CREATE_NEW}
              checkedIconPlaceholder={true}
              text="New user"
            />
          );
        }

        return (
          <OutlinedSelect
            error={form.formState?.errors?.assign?.[accountName]?.message}
            allowUncheck={false}
            className={formRowStyles.selectInput}
            testId="user-select"
            controlledValue={[value[accountName]]}
            renderValue={items => {
              const [item] = items;
              if (item === CREATE_NEW) {
                return "New user";
              }
              return item;
            }}
            onSelect={(items: string[]) => {
              startTransition(() => {
                if (!items.length) {
                  return;
                }
                const [type] = items;
                type === CREATE_NEW
                  ? setValue("assignType", AssignType.create)
                  : setValue("assignType", AssignType.assign);
                onChange({
                  ...value,
                  [accountName]: type,
                });
              });
            }}
            searchOptions={{
              searchPlaceholder: "Search user",
            }}
          >
            {items}
          </OutlinedSelect>
        );
      }}
    />
  );
};

export const AssociateUser = (props: {
  form: UseFormReturn<Fields>;
  serviceAccount?: ServiceAccount;
}) => {
  const { form, serviceAccount } = props;

  const { t } = useTranslation();
  return (
    <FormRow
      label={t("serviceAccounts.modal.associate_user")}
      required={!serviceAccount}
      input={
        <Suspense
          fallback={
            <OutlinedSelect
              disabled
              prefix={<Spinner size={12} />}
            />
          }
        >
          <UsersInput form={form} />
        </Suspense>
      }
    />
  );
};
